import * as React from 'react'
import { FC, PropsWithChildren } from 'react'
import DottedRectangles from '../DottedRectangles/DottedRectangles'
import { cls } from '../../utils/utils'
import ReadyToTalk from '../Section/components/ReadyToTalk/ReadyToTalk'
import Tags from '../Tags/Tags'
import { Parallelepiped } from '../Parallelepiped/Parallelepiped'
import { ROUTE_PATH } from '../../utils/constants'
import styles from './MainSection.module.scss'
import { IFirstSectionProps, ISecondSectionProps } from './Main.types'
import { parallelepipedColors } from './MainSection.constants'

const FirstSection: FC<PropsWithChildren<IFirstSectionProps>> = ({
  children,
  image,
  main,
  className,
  shifted,
  backgroundColor,
  centerBackground = false,
  hideDottedRectanges = false,
  dottedRectanglesClassName,
}) => {
  let mainBg = ''

  if (typeof image === 'string') {
    mainBg = `url(${image})`
  } else {
    if (image?.publicURL) {
      mainBg = `url(${image.publicURL})`
    }
  }

  const internalStyles = {
    backgroundImage: `${mainBg} `,
  }

  return (
    <section
      className={cls(
        styles.firstSection,
        centerBackground && styles.centerBackground,
        main && styles.main,
        className,
        backgroundColor && styles[backgroundColor]
      )}
      style={internalStyles}
    >
      <div className="main-container">
        <div className={styles.container}>{children}</div>
        {!main && !hideDottedRectanges && (
          <DottedRectangles className={cls(styles.rectangles, shifted ? styles.shifted : '', 'mobile-hide', dottedRectanglesClassName)} />
        )}
      </div>
    </section>
  )
}

const SecondSection: FC<PropsWithChildren<ISecondSectionProps>> = ({
  children,
  hideBottomPadding = false,
  smallPadding,
  hideDottedRectanges = false,
  readyToTalkStyle,
  hidePaddingForReady = false,
  readyToTalkText,
  parallelepipedColor,
  tags = null,
  tagsContainerClass = '',
  dontShowParallelepiped = false,
  buttonLink = ROUTE_PATH.connectWithUs.path,
  style = 'grey',
  buttonText,
  className = '',
  innerContainerClassName = '',
  selectedTag = '',
  dottedRectanglesClassName,
  shiftBottom = true,
  dialog,
}) => {
  return (
    <section
      className={cls(
        styles.cancelCollapse,
        styles.secondSection,
        className,
        hideBottomPadding ? styles.dontShowPadding : styles.showUpperPadding,
        !dontShowParallelepiped && styles.addBottomPadding,
        style === 'dark' && styles.darkSection,
        style === 'white' && styles.whiteSection,
        style === 'grey-cropped' && styles.greyCroppedSection,
        style === 'white-cropped' && styles.whiteCroppedSection
      )}
    >
      {!dontShowParallelepiped && <Parallelepiped color={parallelepipedColors[parallelepipedColor || 'white']} />}
      <div className={cls('main-container', styles.secondContainer)}>
        {tags && (
          <div className={cls(styles.tagsContainer, tagsContainerClass)}>
            <Tags tags={tags} className={styles.tag} selectedTag={selectedTag} />
          </div>
        )}
        <div
          className={cls(
            styles.container,
            (tags ?? false) && styles.smallContainer,
            !hideBottomPadding && !smallPadding && styles.showLowerPadding,
            !hidePaddingForReady && !smallPadding && styles.showLowerPadding,
            smallPadding && styles.showSmallPadding,
            innerContainerClassName
          )}
        >
          {children}
        </div>
        {readyToTalkStyle && (
          <ReadyToTalk
            buttonLink={buttonLink}
            colorTitle={readyToTalkStyle}
            buttonText={buttonText}
            shiftBottom={shiftBottom}
            readyToTalkText={readyToTalkText}
            dialog={dialog}
          />
        )}
      </div>
      {!hideDottedRectanges && (
        <>
          <DottedRectangles className={cls(styles.secondRectangles, dottedRectanglesClassName, 'mobile-hide')} />
          <DottedRectangles className={cls(styles.thirdRectangles, dottedRectanglesClassName, 'mobile-hide')} />
        </>
      )}
    </section>
  )
}

export const MainSectionParts = {
  FirstSection,
  SecondSection,
}

const MainSection: FC<PropsWithChildren<{ className?: string }>> = (props) => (
  <div className={cls(styles.mainBackground, props.className)}>{props.children}</div>
)
export default MainSection
